import React from 'react';
import styled, { keyframes } from 'styled-components';
import { PaymentMethod } from '../shared/types';

interface PaymentMethodCardProps {
    type: PaymentMethod;
    label: string;
    isSelected: boolean;
    onSelect: () => void;
    icons: React.ReactNode;
    amount: string;
    onAmountChange: (value: string) => void;
}

export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({
                                                                        type,
                                                                        label,
                                                                        isSelected,
                                                                        onSelect,
                                                                        icons,
                                                                        amount,
                                                                        onAmountChange,
                                                                    }) => {
    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            onAmountChange(value);
        }
    };

    return (
        <Card $selected={isSelected} onClick={onSelect}>
            <CardHeader $type={type}>
                <MethodInfo>
                    <RadioButton $selected={isSelected}>
                        {isSelected && <RadioDot />}
                    </RadioButton>
                    <MethodLabel>{label}</MethodLabel>
                </MethodInfo>
                <IconsWrapper>{icons}</IconsWrapper>
            </CardHeader>
            {isSelected && (
                <InputWrapper>
                    <InputContainer>
                        <Prefix>$</Prefix>
                        <Input
                            type="text"
                            placeholder="0.00"
                            value={amount}
                            onChange={handleAmountChange}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </InputContainer>
                </InputWrapper>
            )}
        </Card>
    );
};

const expandHeight = keyframes`
    from {
        max-height: 0;
        opacity: 0;
    }
    to {
        max-height: 100px;
        opacity: 1;
    }
`;

const Card = styled.div<{ $selected: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border: 1px solid ${props => props.$selected ? '#6777FF' : '#E9E9E9'};
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${props => props.$selected ? '#F8F9FF' : '#FFFFFF'};

    &:hover {
        border-color: ${props => props.$selected ? '#6777FF' : '#D0D0D0'};
    }

    @media (max-width: 768px) {
        padding: 12px;
        gap: 8px;
        border-radius: 10px;
    }
`;

const CardHeader = styled.div<{ $type?: PaymentMethod }>`
    display: flex;
    gap: 12px;
    align-items: start;
    flex-direction: ${p => p.$type === 'crypto' ? 'row': 'column'};
    

    @media (max-width: 768px) {
        gap: 8px;
        flex-wrap: wrap;
    }
`;

const IconsWrapper = styled.div`
    display: flex;
    flex: 1;

    @media (max-width: 768px) {
        order: 3;
    }
`;

const MethodInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;

    @media (max-width: 768px) {
        gap: 8px;
    }
`;

const RadioButton = styled.div<{ $selected: boolean }>`
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1.5px solid ${props => props.$selected ? '#6777FF' : '#D0D0D0'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;

    @media (max-width: 768px) {
        width: 16px;
        height: 16px;
    }
`;

const RadioDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #6777FF;

    @media (max-width: 768px) {
        width: 7px;
        height: 7px;
    }
`;

const MethodLabel = styled.div`
    font-weight: 600;
    font-size: 14px;
    color: #000000;
    font-family: SF Pro Display, sans-serif;
    white-space: nowrap;

    @media (max-width: 768px) {
        font-size: 13px;
    }
`;

const InputWrapper = styled.div`
    animation: ${expandHeight} 0.3s ease-out;
    padding-top: 4px;
`;

const InputContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const Prefix = styled.span`
    position: absolute;
    left: 16px;
    font-size: 14px;
    font-family: SF Pro Display, sans-serif;
    color: #666666;
    pointer-events: none;
    font-weight: 500;

    @media (max-width: 768px) {
        left: 14px;
        font-size: 13px;
    }
`;

const Input = styled.input`
    width: 100%;
    padding: 12px 16px 12px 28px;
    border: 1px solid #D0D0D0;
    border-radius: 8px;
    font-size: 14px;
    font-family: SF Pro Display, sans-serif;
    color: #000000;
    transition: all 0.2s ease;

    &::placeholder {
        color: #A0A0A0;
    }

    &:focus {
        outline: none;
        border-color: #6777FF;
        box-shadow: 0 0 0 3px rgba(103, 119, 255, 0.1);
    }

    @media (max-width: 768px) {
        padding: 10px 14px 10px 26px;
        font-size: 13px;
    }
`;